import React, { useState, useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import {
  Row,
  Col,
  Card,
  Select,
  DatePicker,
  Space,
  Spin,
  Typography,
  Button,
  Statistic,
  Table,
  Tag,
  Tabs,
} from 'antd';
import {
  ReloadOutlined,
  RiseOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { analyticsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface SalesAnalytics {
  total_revenue: number;
  total_orders: number;
  average_order_value: number;
  total_profit: number;
  profit_margin: number;
  growth_rate?: number;
}

interface ChannelPerformance {
  channel_name: string;
  revenue: number;
  orders: number;
  avg_order_value: number;  // Modified field name
  growth_rate: number | null;  // Allow null values
}

interface ProductPerformance {
  product_name: string;
  revenue: number;
  quantity_sold: number;
  profit: number;
  profit_margin: number;
}

interface SalesTrend {
  date: string;
  revenue: number;
  orders: number;
  average_order_value: number;
}

const AnalyticsPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [salesAnalytics, setSalesAnalytics] = useState<SalesAnalytics | null>(null);
  const [channelPerformance, setChannelPerformance] = useState<ChannelPerformance[]>([]);
  const [productPerformance, setProductPerformance] = useState<ProductPerformance[]>([]);
  const [salesTrends, setSalesTrends] = useState<SalesTrend[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);
  const [trendPeriod, setTrendPeriod] = useState('daily');
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>();
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>();
  const { user } = useAuthStore();

  // Channel colors mapping - consistent with Dashboard
  const getChannelColor = (channel: string): string => {
    // 优化的颜色配置，确保高对比度和视觉区分度
    console.log('Analytics - Channel name for color mapping:', channel); // Debug log
    const colorMap: {[key: string]: string} = {
      'Amazon': '#FF9500',        // Bright Orange - Amazon brand color
      'AMAZON': '#FF9500',        // Uppercase variant
      'Home Depot': '#F96302',    // Orange Red - Home Depot brand color
      'HOME_DEPOT': '#F96302',    // Uppercase variant
      'Lowes': '#004CCC',         // Bright Blue - Lowes brand color
      'LOWES': '#004CCC',         // Uppercase variant
      'Bestbuy': '#FFE000',       // Bright Yellow - Best Buy brand color
      'BESTBUY': '#FFE000',       // Uppercase variant
      'Menards': '#DC2626',       // Bright Red - High contrast
      'MENARDS': '#DC2626',       // Uppercase variant
      'Shopify': '#95BF47',       // Green - Shopify brand color
      'SHOPIFY': '#95BF47',       // Uppercase variant
      'Home Depot Canada': '#EA580C', // Darker Orange - distinct from US Home Depot
      'HOME_DEPOT_CA': '#EA580C', // Uppercase variant
      'BestBuy Canada': '#7C3AED', // Purple - distinct from US Best Buy
      'BESTBUY_CA': '#7C3AED',    // Uppercase variant
      'ProMrkt': '#059669',       // Emerald Green - distinct from Shopify
      'PROMRKT': '#059669'        // Uppercase variant
    };
    return colorMap[channel] || '#1890FF'; // Default blue for unmapped channels
  };

  // Channel abbreviation mapping - for Matrix scatter plot only
  const getChannelAbbreviation = (channelName: string): string => {
    const abbreviations: {[key: string]: string} = {
      'Home Depot': 'HD',
      'HOME_DEPOT': 'HD',
      'Amazon': 'AM',
      'AMAZON': 'AM',
      'BestBuy Canada': 'BC',
      'BESTBUY': 'BB',
      'BESTBUY_CANADA': 'BC',
      'Home Depot Canada': 'HC',
      'HOME_DEPOT_CANADA': 'HC',
      'ProMrkt': 'PM',
      'PROMRKT': 'PM',
      'LOWES': 'LW',
      'Lowes': 'LW',
      'MENARDS': 'MD',
      'Menards': 'MD'
    };
    return abbreviations[channelName] || channelName.substring(0, 2).toUpperCase();
  };

  // Channel name display - responsive based on screen size
  const getChannelDisplayName = (channelName: string): string => {
    if (isMobile) {
      // Mobile: use abbreviations
      return getChannelAbbreviation(channelName);
    } else {
      // Desktop/Monitor: use full names
      const displayNames: {[key: string]: string} = {
        'HOME_DEPOT': 'Home Depot',
        'AMAZON': 'Amazon',
        'BESTBUY': 'BestBuy',
        'BESTBUY_CANADA': 'BestBuy CA',
        'BESTBUY_CA': 'BestBuy CA',
        'HOME_DEPOT_CANADA': 'Home Depot CA',
        'HOME_DEPOT_CA': 'Home Depot CA',
        'PROMRKT': 'PRO Market',
        'ProMrkt': 'PRO Market',
        'LOWES': 'Lowes',
        'MENARDS': 'Menards'
      };
      return displayNames[channelName] || channelName;
    }
  };

  const isAnalyst = user?.role === 'analyst' || user?.role === 'admin';

  // Get sales analytics data
  const fetchSalesAnalytics = async () => {
    try {
      const params: any = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      if (selectedChannel) params.channel = selectedChannel;
      if (selectedRegion) params.region = selectedRegion;
      
      const response = await analyticsAPI.getSalesAnalytics(params);
      setSalesAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch sales analytics data:', error);
    }
  };

  // Get channel performance data
  const fetchChannelPerformance = async () => {
    try {
      const params: any = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      const response = await analyticsAPI.getChannelPerformance(params);
      setChannelPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch channel performance data:', error);
    }
  };

  // Get product performance data
  const fetchProductPerformance = async () => {
    try {
      const params: any = { limit: 20, sort_by: 'revenue' };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      const response = await analyticsAPI.getProductPerformance(params);
      setProductPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch product performance data:', error);
    }
  };

  // Get sales trends data
  const fetchSalesTrends = async () => {
    try {
      const params: any = { period: trendPeriod };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      const response = await analyticsAPI.getSalesTrends(params);
      setSalesTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch sales trends data:', error);
    }
  };

  // Get all data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSalesAnalytics(),
        fetchChannelPerformance(),
        fetchProductPerformance(),
        fetchSalesTrends(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSalesAnalytics(),
          fetchChannelPerformance(),
          fetchProductPerformance(),
          fetchSalesTrends(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, trendPeriod, selectedChannel, selectedRegion]);

  // Sales trend chart configuration
  const getSalesTrendOption = () => {
    if (!salesTrends.length) return {};
    
    return {
      title: {
        text: 'Sales Trend Analysis',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#1D1D1F',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      legend: {
        data: ['Revenue', 'Orders', 'Average Order Value'],
        bottom: 0,
      },
      xAxis: {
        type: 'category',
        data: salesTrends.map(item => dayjs(item.date).format('MM-DD')),
        axisLine: {
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          color: '#86909C',
        },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Revenue ($)',
          position: 'left',
          axisLabel: {
            color: '#86909C',
            formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
          },
          axisLine: {
            lineStyle: { color: '#E5E5E5' },
          },
          splitLine: {
            lineStyle: { color: '#F5F5F5' },
          },
        },
        {
          type: 'value',
          name: 'Orders',
          position: 'right',
          axisLabel: {
            color: '#86909C',
          },
          axisLine: {
            lineStyle: { color: '#E5E5E5' },
          },
        },
      ],
      series: [
        {
          name: 'Revenue',
          type: 'line',
          yAxisIndex: 0,
          data: salesTrends.map(item => item.revenue),
          smooth: true,
          lineStyle: {
            color: '#0066CC',
            width: 3,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(0, 102, 204, 0.3)' },
                { offset: 1, color: 'rgba(0, 102, 204, 0.05)' },
              ],
            },
          },
        },
        {
          name: 'Orders',
          type: 'bar',
          yAxisIndex: 1,
          data: salesTrends.map(item => item.orders),
          itemStyle: {
            color: '#76B5DD',
          },
        },
        {
          name: 'Average Order Value',
          type: 'line',
          yAxisIndex: 0,
          data: salesTrends.map(item => item.average_order_value),
          smooth: true,
          lineStyle: {
            color: '#52C41A',
            width: 2,
          },
        },
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true,
      },
    };
  };

  // Channel performance chart configuration
  const getChannelPerformanceOption = () => {
    if (!channelPerformance.length) return {};
    
    return {
      title: {
        text: 'Channel Performance Comparison',
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#1D1D1F',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: ['Revenue', 'Orders'],
        bottom: 0,
      },
      xAxis: {
        type: 'category',
        data: channelPerformance.map(item => getChannelDisplayName(item.channel_name)),
        axisLabel: {
          color: '#86909C',
          interval: 0,
          rotate: 45,
        },
        axisLine: {
          lineStyle: { color: '#E5E5E5' },
        },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Revenue ($)',
          position: 'left',
          axisLabel: {
            color: '#86909C',
            formatter: (value: number) => `$${(value / 1000).toFixed(0)}K`,
          },
          axisLine: {
            lineStyle: { color: '#E5E5E5' },
          },
          splitLine: {
            lineStyle: { color: '#F5F5F5' },
          },
        },
        {
          type: 'value',
          name: 'Orders',
          position: 'right',
          axisLabel: {
            color: '#86909C',
          },
          axisLine: {
            lineStyle: { color: '#E5E5E5' },
          },
        },
      ],
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          yAxisIndex: 0,
          data: channelPerformance.map(item => ({
            value: item.revenue,
            itemStyle: {
              color: getChannelColor(item.channel_name),
            },
          })),
        },
        {
          name: 'Orders',
          type: 'line',
          yAxisIndex: 1,
          data: channelPerformance.map(item => item.orders),
          lineStyle: {
            color: '#52C41A',
            width: 3,
          },
          symbol: 'circle',
          symbolSize: 8,
        },
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true,
      },
    };
  };

  // Product performance table columns
  const productColumns = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value: number) => `$${(value || 0).toFixed(2)}`,
      sorter: (a: ProductPerformance, b: ProductPerformance) => a.revenue - b.revenue,
    },
    {
      title: 'Sales Volume',
      dataIndex: 'quantity_sold',
      key: 'quantity_sold',
      sorter: (a: ProductPerformance, b: ProductPerformance) => a.quantity_sold - b.quantity_sold,
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      render: (value: number) => `$${(value || 0).toFixed(2)}`,
      sorter: (a: ProductPerformance, b: ProductPerformance) => a.profit - b.profit,
    },
    {
      title: 'Profit Margin',
      dataIndex: 'profit_margin',
      key: 'profit_margin',
      render: (value: number) => {
        const safeValue = value || 0;
        const color = safeValue > 30 ? 'green' : safeValue > 15 ? 'orange' : 'red';
        return <Tag color={color}>{safeValue.toFixed(1)}%</Tag>;
      },
      sorter: (a: ProductPerformance, b: ProductPerformance) => a.profit_margin - b.profit_margin,
    },
  ];

  // Channel performance table columns
  const channelColumns = [
    {
      title: 'Channel Name',
      dataIndex: 'channel_name',
      key: 'channel_name',
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value: number) => `$${(value || 0).toFixed(2)}`,
      sorter: (a: ChannelPerformance, b: ChannelPerformance) => a.revenue - b.revenue,
    },
    {
      title: 'Orders',
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a: ChannelPerformance, b: ChannelPerformance) => a.orders - b.orders,
    },
    {
      title: 'Average Order Value',
      dataIndex: 'avg_order_value',  // Modified field name
      key: 'avg_order_value',
      render: (value: number) => `$${(value || 0).toFixed(2)}`,
      sorter: (a: ChannelPerformance, b: ChannelPerformance) => a.avg_order_value - b.avg_order_value,
    },
    {
      title: 'Growth Rate',
      dataIndex: 'growth_rate',
      key: 'growth_rate',
      render: (value: number | null) => {
        if (value === null || value === undefined) {
          return <Tag color="gray">No Data</Tag>;
        }
        const color = value > 0 ? 'green' : 'red';
        const icon = value > 0 ? '↑' : '↓';
        return <Tag color={color}>{icon} {Math.abs(value).toFixed(1)}%</Tag>;
      },
      sorter: (a: ChannelPerformance, b: ChannelPerformance) => (a.growth_rate || 0) - (b.growth_rate || 0),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Page Title and Controls */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Data Analytics</Title>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Select Channel"
              value={selectedChannel}
              onChange={setSelectedChannel}
              allowClear
              style={{ width: 120 }}
            >
              <Select.Option value="online">Online Store</Select.Option>
              <Select.Option value="offline">Offline Store</Select.Option>
              <Select.Option value="phone">Phone Sales</Select.Option>
              <Select.Option value="agent">Agent</Select.Option>
            </Select>
            <Select
              placeholder="Select Region"
              value={selectedRegion}
              onChange={setSelectedRegion}
              allowClear
              style={{ width: 120 }}
            >
              <Select.Option value="north">North China</Select.Option>
              <Select.Option value="south">South China</Select.Option>
              <Select.Option value="east">East China</Select.Option>
              <Select.Option value="west">West China</Select.Option>
            </Select>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              allowClear
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchAllData}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Key Metrics Cards */}
      {salesAnalytics && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={salesAnalytics.total_revenue}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                suffix={
                  salesAnalytics.growth_rate !== undefined && (
                    <span style={{ fontSize: 14, color: salesAnalytics.growth_rate >= 0 ? '#52c41a' : '#ff4d4f' }}>
                      <RiseOutlined /> {salesAnalytics.growth_rate.toFixed(1)}%
                    </span>
                  )
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Orders"
                value={salesAnalytics.total_orders}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Average Order Value"
                value={salesAnalytics.average_order_value}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Profit Margin"
                value={salesAnalytics.profit_margin}
                prefix={<PercentageOutlined />}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts and Tables Area */}
      <Tabs defaultActiveKey="trends" type="card">
        <TabPane tab="Sales Trends" key="trends">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <span>Time Period:</span>
                    <Select
                      value={trendPeriod}
                      onChange={setTrendPeriod}
                      style={{ width: 120 }}
                    >
                      <Select.Option value="daily">Daily</Select.Option>
                      <Select.Option value="weekly">Weekly</Select.Option>
                      <Select.Option value="monthly">Monthly</Select.Option>
                    </Select>
                  </Space>
                </div>
                <ReactECharts
                  option={getSalesTrendOption()}
                  style={{ height: '500px' }}
                  opts={{ renderer: 'svg' }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
        
        <TabPane tab="Channel Analysis" key="channels">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Card>
                <ReactECharts
                  option={getChannelPerformanceOption()}
                  style={{ height: '400px' }}
                  opts={{ renderer: 'svg' }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card title="Channel Performance Details">
                <Table
                  columns={channelColumns}
                  dataSource={channelPerformance}
                  rowKey="channel_name"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
        
        <TabPane tab="Product Analysis" key="products">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="Product Performance Ranking">
                <Table
                  columns={productColumns}
                  dataSource={productPerformance}
                  rowKey="product_name"
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
        
        {isAnalyst && (
          <TabPane tab="Advanced Analysis" key="advanced">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="Customer Cohort Analysis">
                  <div style={{ textAlign: 'center', padding: '50px 0', color: '#86909C' }}>
                    <p>Customer cohort analysis feature is under development...</p>
                    <Button 
                      type="primary" 
                      onClick={() => {
                        // Call cohort analysis API here
                        console.log('Cohort analysis');
                      }}
                    >
                      Generate Analysis Report
                    </Button>
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Sales Forecast">
                  <div style={{ textAlign: 'center', padding: '50px 0', color: '#86909C' }}>
                    <p>Sales forecast feature is under development...</p>
                    <Button 
                      type="primary" 
                      onClick={() => {
                        // Call sales forecast API here
                        console.log('Sales forecast');
                      }}
                    >
                      Generate Forecast Report
                    </Button>
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;